import { useEffect, useRef, useState } from 'react';
import type { CodeItem, CodeToolItem } from '../lib/types';

function Dots() {
  return (
    <span className="inline-flex gap-1" aria-hidden>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="h-1.5 w-1.5 rounded-full bg-seal"
          style={{ animation: `pulseDot 1s ease-in-out ${i * 0.2}s infinite` }}
        />
      ))}
    </span>
  );
}

function Caret({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function renderText(text: string) {
  // fenced ``` blocks render in a horizontally-scrollable <pre>; prose wraps
  return text.split(/```/).map((p, i) =>
    i % 2 === 1 ? (
      <pre
        key={i}
        className="my-1 max-h-72 overflow-auto rounded-md surface-2 p-2 font-mono text-[11px] leading-relaxed"
      >
        {p.replace(/^[\w-]*\n/, '')}
      </pre>
    ) : (
      <span key={i} className="whitespace-pre-wrap break-words">
        {p}
      </span>
    ),
  );
}

function toolArg(input: string): string {
  try {
    const o = JSON.parse(input) as Record<string, unknown>;
    const pick =
      o.command ?? o.file_path ?? o.path ?? o.pattern ?? o.url ?? o.description ?? o.prompt;
    if (typeof pick === 'string') return pick;
    const firstStr = Object.values(o).find((v) => typeof v === 'string');
    return typeof firstStr === 'string' ? firstStr : '';
  } catch {
    return '';
  }
}

function ToolCard({ item }: { item: CodeToolItem }) {
  const [open, setOpen] = useState(false);
  const arg = toolArg(item.input);
  return (
    <div className={`rounded-lg border text-xs ${item.isError ? 'border-red-500/40' : 'bd'}`}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left"
      >
        <Caret open={open} />
        <span className="shrink-0 font-mono font-semibold text-air">{item.name}</span>
        <span className="min-w-0 flex-1 truncate font-mono text-muted">{arg}</span>
        {item.result === undefined ? (
          <span className="shrink-0 text-[10px] text-faint">…</span>
        ) : item.isError ? (
          <span className="shrink-0 text-[10px] font-semibold text-red-400">ERR</span>
        ) : (
          <span className="shrink-0 text-[10px] text-air">✓</span>
        )}
      </button>
      {open && (
        <div className="space-y-2 border-t bd px-2.5 py-2">
          <pre className="max-h-60 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] text-muted">
            {item.input}
          </pre>
          {item.result !== undefined && (
            <pre
              className={`max-h-60 overflow-auto whitespace-pre-wrap break-words font-mono text-[11px] ${
                item.isError ? 'text-red-300' : 'text-fg/70'
              }`}
            >
              {item.result || '(no output)'}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}

function Item({ item }: { item: CodeItem }) {
  if (item.role === 'tool') return <ToolCard item={item} />;
  if (item.role === 'note')
    return <div className="py-1 text-center text-[11px] text-faint">{item.text}</div>;
  if (item.role === 'user')
    return (
      <div className="flex justify-end">
        <div className="max-w-[82%] rounded-2xl surface-2 px-3 py-2 text-sm">
          <div className="whitespace-pre-wrap break-words">{item.text}</div>
        </div>
      </div>
    );
  if (item.role === 'error')
    return (
      <div className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
        <div className="whitespace-pre-wrap break-words">{item.text}</div>
      </div>
    );
  // assistant
  return (
    <div className="text-sm leading-relaxed text-fg/90">{renderText(item.text)}</div>
  );
}

export function CodeTranscript({
  transcript,
  working,
  agentName,
}: {
  transcript: CodeItem[];
  working: boolean;
  agentName: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const atBottomRef = useRef(true);
  const [showJump, setShowJump] = useState(false);
  const [stalled, setStalled] = useState(false);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    atBottomRef.current = atBottom;
    setShowJump(!atBottom);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (atBottomRef.current) {
      el.scrollTop = el.scrollHeight;
      setShowJump(false);
    }
  }, [transcript, working]);

  const jump = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setShowJump(false);
  };

  const last = transcript[transcript.length - 1];
  const starting = working && (!last || last.role === 'user');

  useEffect(() => {
    setStalled(false);
    if (!starting) return;
    const t = setTimeout(() => setStalled(true), 20000);
    return () => clearTimeout(t);
  }, [starting, transcript.length]);

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="absolute inset-0 space-y-3 overflow-auto py-3"
        style={{
          maskImage: 'linear-gradient(to bottom, transparent, #000 5%, #000 96%, transparent)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, #000 5%, #000 96%, transparent)',
        }}
      >
        {transcript.length === 0 && !working && (
          <div className="px-2 text-center text-sm text-faint">
            和 {agentName} 直接对话，让它改 claudio 的代码。
            <br />
            比如「列出 server/src 里的文件」或「把播放按钮改大一点」。
          </div>
        )}
        {transcript.map((it, i) => (
          <Item key={i} item={it} />
        ))}
        {starting ? (
          <div className="flex items-center gap-2 text-xs text-faint">
            <Dots />{' '}
            {stalled ? `仍在等待 ${agentName}…（可点上方 ◼ 停止）` : `正在启动 ${agentName}…`}
          </div>
        ) : (
          working && <Dots />
        )}
      </div>

      {showJump && (
        <button
          onClick={jump}
          className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full surface-2 px-3 py-1 text-xs text-fg shadow border bd"
        >
          ↓ 新消息
        </button>
      )}
    </div>
  );
}
